/**
 * NEON — Carrusel de reseñas (Judge.me) con movimiento continuo en móvil.
 *
 * Segunda versión de este script. La primera simulaba un clic en la
 * flecha "siguiente" de Judge.me cada pocos segundos — funcionaba (se
 * confirmó en un móvil real), pero David pidió que las reseñas se
 * muevan de forma CONTINUA, como una cinta, no a saltos cada X
 * segundos. Un clic en la flecha de Judge.me siempre da un salto (pasa
 * de una reseña a la siguiente de golpe), por diseño de su propio
 * widget — no hay forma de pedirle a Judge.me que se deslice solo poco
 * a poco, así que un simple cambio de intervalo no podía resolver esto.
 *
 * Además, al investigar por dentro el carrusel de Judge.me
 * (.jm-carousel) se vio que su elemento contenedor tiene `width: 0`
 * fijado por su propio CSS (con los recuadros de reseña posicionados
 * "fuera" de esa caja) — una forma bastante particular de montarlo que
 * lo hace demasiado frágil para animar directamente sin arriesgarse a
 * que un futuro cambio de Judge.me lo rompa.
 *
 * Por eso este script NO toca ni anima el carrusel de Judge.me en sí:
 * clona las tarjetas de reseña YA renderizadas por Judge.me (mismo
 * texto, mismas estrellas, mismo nombre — reseñas reales, no se
 * inventa nada) dentro de una cinta propia del tema que se desliza sola
 * con una animación CSS en bucle infinito. El widget de Judge.me sigue
 * existiendo en la página tal cual lo generó la app, solo se le pone
 * "display:none" por CSS — nada de su contenido se borra ni se modifica.
 *
 * TERCERA VERSIÓN (portada de Halloween, sep 2026) — filtro opcional por
 * producto: el bloque de Judge.me en modo "auto" identifica el producto
 * vía el objeto `product` de Liquid, que NO existe en páginas sin
 * producto propio (como la portada) — ahí mezclaba reseñas de la
 * máscara con las del corrector de postura. No se puede pedirle a
 * Judge.me que filtre por producto vía sus propios ajustes: ese campo
 * (`products`) no se resuelve si no se elige desde el picker visual del
 * bloque (mismo bug ya visto con ajustes nativos `type: "product"`), y
 * tocar las tarjetas del carrusel VIVO de Judge.me desde fuera (Vue) es
 * arriesgado — así que en vez de eso, cuando `neon-titulo-seccion.liquid`
 * marca un producto a filtrar (atributo `data-neon-filtro-resenas`, ver
 * ese archivo), esta cinta:
 *   1. se construye en TODAS las pantallas (no solo en móvil),
 *   2. solo clona las tarjetas cuyo nombre de producto coincide, y
 *   3. oculta el widget nativo de Judge.me en TODAS las pantallas
 *      (nunca debe verse la mezcla, ni siquiera un instante).
 * Sin ese atributo (ficha de producto normal), el comportamiento es
 * exactamente el de siempre: cinta solo en móvil, sin filtrar nada.
 *
 * BUG REAL ENCONTRADO Y CORREGIDO (David: "en ordenador solo veo el
 * título, las reseñas no aparecen") — Judge.me pinta sus tarjetas de
 * forma PROGRESIVA (Vue), no todas de golpe. La versión anterior
 * arrancaba en cuanto veía LA PRIMERA tarjeta en el DOM y, si en ese
 * instante aún no había las 2 mínimas (o, con filtro, menos de 2 del
 * producto correcto), se rendía para siempre: el MutationObserver se
 * desconectaba sin reintentar, pero el widget nativo YA se había
 * ocultado — resultado, título visible y nada debajo, permanente. Ahora
 * el observador sigue vivo y reintenta en cada cambio del DOM hasta
 * tener tarjetas suficientes (o hasta el tope de 20s), y el widget
 * nativo solo se oculta en el momento en que la cinta propia SÍ se va a
 * construir — así, en el peor caso (que ya no debería darse), se ve el
 * carrusel original en vez de un hueco vacío.
 *
 * FILTRO DE CALIDAD DE DATOS (oct 2026) — las 14 reseñas importadas de
 * AliExpress vía Judge.me Importer traen problemas reales de la propia
 * importación, no del tema: dos parejas de reseñas EXACTAMENTE
 * duplicadas (mismo texto y fecha, firmadas por "clientes" distintos) y
 * una reseña vacía cuyo "cuerpo" es literalmente el nombre del
 * producto, sin ninguna opinión real. Lo correcto es ocultarlas desde
 * el propio panel de Judge.me (viven en su base de datos, no en el
 * tema) — mientras eso no se haga (o como protección ante futuras
 * importaciones con el mismo problema), esta cinta descarta esas
 * tarjetas de baja calidad ANTES de clonarlas, para que al menos la
 * vista móvil no repita texto ni muestre una tarjeta en blanco. El
 * carrusel nativo de escritorio no se toca — sigue mostrando las 14 tal
 * cual las gestione Judge.me.
 */
(function () {
  'use strict';

  var SELECTOR_WIDGET = '.jdgm-review-snippet-widget-v2';
  var SELECTOR_TARJETA = '.jdgm-rev-snippet-card';
  var SELECTOR_NOMBRE_PRODUCTO = '.jdgm-rev-snippet-card__product-name';
  var SELECTOR_CUERPO = '.jdgm-rev-snippet-card__body';
  var SELECTOR_NOMBRE = '.jdgm-rev-snippet-card__reviewer-name';
  var PX_POR_SEGUNDO = 45; // velocidad de la cinta — constante, cómoda de leer.
  var PAUSA_TRAS_TOQUE_MS = 2500;
  var MIN_CARACTERES_CUERPO = 10; // por debajo de esto se considera "sin opinión real".

  var mediaMovil = window.matchMedia('(max-width: 749px)');
  var mediaMenosMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)');

  var marcadorFiltro = document.querySelector('[data-neon-filtro-resenas]');
  var tituloFiltro = marcadorFiltro ? marcadorFiltro.getAttribute('data-neon-filtro-resenas') : null;
  var construirSiempre = !!tituloFiltro; // true = portada con filtro; false = ficha normal.

  if (mediaMenosMovimiento.matches) return;
  if (!construirSiempre && !mediaMovil.matches) return;

  var yaConstruida = false;

  function construirCinta(widget) {
    var todas = [].slice.call(widget.querySelectorAll(SELECTOR_TARJETA));
    var tarjetas = tituloFiltro
      ? todas.filter(function (t) {
          var nombreEl = t.querySelector(SELECTOR_NOMBRE_PRODUCTO);
          return nombreEl && nombreEl.textContent.trim() === tituloFiltro;
        })
      : todas;

    // NEON: descarta tarjetas de baja calidad ANTES de clonarlas (ver
    // comentario de cabecera, "FILTRO DE CALIDAD DE DATOS") — tres cosas:
    // 1) sin opinión real (cuerpo vacío, o el cuerpo es literalmente el
    //    nombre del producto, caso visto con una reseña real importada),
    // 2) duplicado exacto de otra tarjeta ya aceptada (mismo texto,
    //    normalizado recortando espacios) — se queda solo la primera,
    // 3) autor concreto ya identificado como copia duplicada a mano (ver
    //    lista fija más abajo) — necesario ADEMÁS del punto 2 porque, en
    //    cuanto neon-resenas-traduccion.js traduce solo UNA copia de cada
    //    pareja duplicada (a propósito, la otra se deja en inglés — ver
    //    ese archivo), el texto de las dos deja de coincidir y el punto 2
    //    ya no las detecta como duplicadas. La lista fija cubre justo ese
    //    caso conocido; el punto 2 se deja igualmente, como red de
    //    seguridad genérica ante futuras importaciones con duplicados que
    //    aún no se hayan traducido.
    var AUTORES_DUPLICADOS_CONOCIDOS = { 'king kris': true, 'kelli breitenberg': true };
    var textosVistos = {};
    tarjetas = tarjetas.filter(function (t) {
      var nombreAutorEl = t.querySelector(SELECTOR_NOMBRE);
      var nombreAutor = nombreAutorEl ? nombreAutorEl.textContent.replace(/\s+/g, ' ').trim().toLowerCase() : '';
      if (AUTORES_DUPLICADOS_CONOCIDOS[nombreAutor]) return false;

      var cuerpoEl = t.querySelector(SELECTOR_CUERPO);
      var cuerpo = cuerpoEl ? cuerpoEl.textContent.replace(/\s+/g, ' ').trim() : '';
      if (cuerpo.length < MIN_CARACTERES_CUERPO) return false;

      var nombreProductoEl = t.querySelector(SELECTOR_NOMBRE_PRODUCTO);
      var nombreProducto = nombreProductoEl ? nombreProductoEl.textContent.replace(/\s+/g, ' ').trim() : '';
      if (nombreProducto && cuerpo === nombreProducto) return false;

      var clave = cuerpo.toLowerCase();
      if (textosVistos[clave]) return false;
      textosVistos[clave] = true;

      return true;
    });

    // NEON: con menos de 2 tarjetas (Judge.me aún las está pintando, o
    // de verdad no hay suficientes reseñas) NO se hace nada todavía —
    // ni se oculta el widget nativo ni se da por fracasado. Se sale sin
    // marcar `yaConstruida`, así el MutationObserver de más abajo vuelve
    // a intentarlo en el siguiente cambio del DOM.
    if (tarjetas.length < 2) return;

    if (construirSiempre) {
      // NEON: con filtro activo, el widget nativo (con la mezcla de
      // productos) no debe verse — pero solo se oculta AHORA, justo
      // cuando ya sabemos que la cinta propia sí se va a construir con
      // datos suficientes, nunca antes.
      var estiloOcultarFiltro = document.createElement('style');
      estiloOcultarFiltro.textContent = SELECTOR_WIDGET + ' { display: none !important; }';
      document.head.appendChild(estiloOcultarFiltro);
    } else {
      // Oculta el carrusel original de Judge.me SOLO en este ancho de
      // pantalla — en escritorio no se toca nada (ficha de producto
      // normal, sin filtro).
      var estiloOcultar = document.createElement('style');
      estiloOcultar.textContent =
        '@media (max-width: 749px) { ' + SELECTOR_WIDGET + ' { display: none !important; } }';
      document.head.appendChild(estiloOcultar);
    }

    yaConstruida = true;

    // NEON: traduce cada tarjeta ANTES de clonarla, para que las copias
    // de la cinta ya salgan en español — ver assets/neon-resenas-traduccion.js
    // (se carga antes que este archivo). Llamada de seguridad: ese script
    // ya observa el DOM por su cuenta y normalmente llega antes, pero si
    // esta cinta se construyera primero por cualquier motivo de tiempos,
    // esto garantiza que las copias no se queden en inglés.
    if (window.neonTraducirTarjeta) {
      tarjetas.forEach(function (t) { window.neonTraducirTarjeta(t); });
    }

    var contenedor = document.createElement('div');
    contenedor.className = 'neon-resenas-cinta';

    var pista = document.createElement('div');
    pista.className = 'neon-resenas-cinta__pista';

    // Se duplica el set de tarjetas una vez (mismo contenido, clonado
    // del DOM real de Judge.me) para poder animar un bucle perfecto:
    // al llegar al -50% de la pista, visualmente es indistinguible del
    // principio, así que el bucle no se nota.
    [tarjetas, tarjetas].forEach(function (grupo) {
      grupo.forEach(function (tarjeta) {
        pista.appendChild(tarjeta.cloneNode(true));
      });
    });

    contenedor.appendChild(pista);
    widget.insertAdjacentElement('afterend', contenedor);

    // La duración se calcula a partir de la posición real donde empieza
    // el segundo grupo (el clon) — no de "scrollWidth / 2", que se
    // desviaría medio hueco (gap) y se notaría un pequeño salto en cada
    // vuelta del bucle. Así el bucle es perfecto sin importar el gap.
    var anchoUnGrupo = pista.children[tarjetas.length].offsetLeft - pista.children[0].offsetLeft;
    var duracionSegundos = anchoUnGrupo / PX_POR_SEGUNDO;
    pista.style.setProperty('--neon-cinta-ancho', anchoUnGrupo + 'px');
    pista.style.setProperty('--neon-cinta-duracion', duracionSegundos + 's');

    var pausadoHastaTimeout = null;
    function pausarTemporalmente() {
      pista.style.animationPlayState = 'paused';
      if (pausadoHastaTimeout) clearTimeout(pausadoHastaTimeout);
      pausadoHastaTimeout = setTimeout(function () {
        pista.style.animationPlayState = 'running';
      }, PAUSA_TRAS_TOQUE_MS);
    }
    contenedor.addEventListener('touchstart', pausarTemporalmente, { passive: true });
    contenedor.addEventListener('pointerdown', pausarTemporalmente);
  }

  function buscarWidgetYArrancar() {
    var widget = document.querySelector(SELECTOR_WIDGET);
    if (widget && widget.querySelector(SELECTOR_TARJETA)) {
      construirCinta(widget);
    }
    return yaConstruida;
  }

  if (buscarWidgetYArrancar()) return;

  // El widget de Judge.me se pinta de forma asíncrona, y además de forma
  // PROGRESIVA (tarjeta a tarjeta, no todas de golpe) — se sigue
  // observando el DOM y reintentando en cada cambio, no solo en el
  // primero, hasta que construirCinta consiga tarjetas suficientes o se
  // llegue al tope de 20s.
  var observador = new MutationObserver(function () {
    if (buscarWidgetYArrancar()) observador.disconnect();
  });
  observador.observe(document.body, { childList: true, subtree: true });
  setTimeout(function () {
    observador.disconnect();
  }, 20000);
})();
